import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

/* ─────────────────────────────────────────────────────────────────────────────
   TryMedocx — the app's feed, on the web.

   One category, three feeds, one page per swipe. The data comes from
   public/try-feed.json, which tools/make_try_feed.py writes; nothing is
   bundled, so replacing that file is the whole update path.

   ── WHAT MIRRORS THE APP, AND WHY ──
   · Vertical scroll-snap instead of UIPageViewController. Same contract: one
     item per page, one page per screen, no nested scrolling that could swallow
     the swipe (the card pages use overflow:auto, which only takes the gesture
     when there is genuinely something to scroll).
   · Only the page in view plays, and only its immediate neighbours even get a
     <video> element — the browser equivalent of the app's preload window. Four
     uncapped downloads sharing one connection is exactly wrong at the one
     moment that matters.
   · Answers and reveals live HERE, keyed by item id, not inside the page
     components — a page that scrolls out and back must not forget what you
     told it. Same reason FeedModel owns questionStates.
   · Autoplay starts MUTED, because every browser refuses sound without a
     gesture. The sound button is that gesture, and the hint on the first page
     is the only thing that makes it discoverable.

   ── TWO DELIBERATE DIFFERENCES FROM THE APP ──
   · A video carries NO caption. The bar at the top already names the category
     on every page, and the app's caption exists because its feed mixes topics —
     this one doesn't, so the same words in the corner of every clip were the
     same fact stated twice, over the artwork.
   · A flashcard has no Hard / Easy. Those grades feed the app's Troubling
     Spots pile, and there is no pile here — a button that files something
     nowhere is a promise the page can't keep. Turn the card over, read it,
     keep scrolling.
   ───────────────────────────────────────────────────────────────────────── */

const BASE =
  (typeof process !== "undefined" && process.env && process.env.PUBLIC_URL) || "";
const FEED_URL = `${BASE}/try-feed.json`;

const MODES = [
  { id: "videos", label: "Short Videos", key: "videos" },
  { id: "questions", label: "Quick Questions", key: "questions" },
  { id: "flashcards", label: "Flashcards", key: "flashcards" },
];

const EMPTY_COPY = {
  videos: ["No videos yet", "This category has no videos in try-feed.json."],
  questions: ["No questions yet", "This category has no questions in try-feed.json."],
  flashcards: ["No flashcards yet", "This category has no cards in try-feed.json."],
};

/* ── Icons. Inline so the page carries no dependency. ─────────────────────── */

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8 5.2v13.6L19 12z" /></svg>
);

const SpeakerOn = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path fill="currentColor" d="M4 9h3.4L12 5.2v13.6L7.4 15H4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1z" />
    <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
          d="M15.6 9.3a3.9 3.9 0 0 1 0 5.4M18.2 7a7.4 7.4 0 0 1 0 10" />
  </svg>
);

const SpeakerOff = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path fill="currentColor" d="M4 9h3.4L12 5.2v13.6L7.4 15H4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1z" />
    <path fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"
          d="M16 10l4.5 4.5M20.5 10L16 14.5" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"
          strokeLinejoin="round" d="M5 12.6l4.6 4.6L19 7" />
  </svg>
);

const CrossIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"
          d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
  </svg>
);

const ChevronDown = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"
          strokeLinejoin="round" d="M6 9.5l6 6 6-6" />
  </svg>
);

const TapIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="3.1" fill="currentColor" />
    <path fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
          d="M6.8 6.8a7.4 7.4 0 0 0 0 10.4M17.2 6.8a7.4 7.4 0 0 1 0 10.4" />
  </svg>
);

/* ── The caption on a card: subject above, category below ─────────────────────
   Cards only. A video page states nothing — the top bar has already named the
   category, and this feed is one category deep. */

function TopicLabel({ subject, topic }) {
  return (
    <div className="try-cap">
      {subject ? <div className="try-cap-sub">{subject}</div> : null}
      <div className="try-cap-top">{topic}</div>
    </div>
  );
}

/* ── One video page ───────────────────────────────────────────────────────── */

function VideoPage({ video, active, near, muted, onEnded }) {
  const videoRef = useRef(null);
  const barRef = useRef(null);
  const draggingRef = useRef(false);
  const userPausedRef = useRef(false);

  const [paused, setPaused] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [ratio, setRatio] = useState(0);

  // Play when this page is the one in view; stop and rewind when it isn't. The
  // rewind matters: coming back to four seconds of remaining video is worse
  // than starting again.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (active) {
      userPausedRef.current = false;
      const started = el.play();
      if (started && typeof started.catch === "function") started.catch(() => {});
    } else {
      el.pause();
      try { el.currentTime = 0; } catch (err) { /* not seekable yet */ }
      setRatio(0);
      setWaiting(false);
    }
  }, [active, near]);

  // React sets `muted` as a property, but a freshly mounted element in the
  // preload window can miss it, so state it explicitly too.
  useEffect(() => {
    const el = videoRef.current;
    if (el) el.muted = muted;
  }, [muted, near]);

  const toggle = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      userPausedRef.current = false;
      const started = el.play();
      if (started && typeof started.catch === "function") started.catch(() => {});
    } else {
      userPausedRef.current = true;
      el.pause();
    }
  };

  const seekFrom = (clientX) => {
    const el = videoRef.current;
    const bar = barRef.current;
    if (!el || !bar || !el.duration) return;
    const box = bar.getBoundingClientRect();
    const next = Math.min(Math.max((clientX - box.left) / box.width, 0), 1);
    el.currentTime = next * el.duration;
    setRatio(next);
  };

  return (
    <div className="try-video">
      {near ? (
        <video
          ref={videoRef}
          src={video.url}
          playsInline
          muted={muted}
          preload={active ? "auto" : "metadata"}
          onPlay={() => setPaused(false)}
          onPause={() => setPaused(true)}
          onWaiting={() => setWaiting(true)}
          onPlaying={() => { setWaiting(false); setPaused(false); }}
          onCanPlay={() => {
            const el = videoRef.current;
            if (!el || !active || userPausedRef.current || !el.paused) return;
            const started = el.play();
            if (started && typeof started.catch === "function") started.catch(() => {});
          }}
          onTimeUpdate={() => {
            const el = videoRef.current;
            if (!el || draggingRef.current || !el.duration) return;
            setRatio(el.currentTime / el.duration);
          }}
          onEnded={onEnded}
        />
      ) : (
        <div className="try-blank-frame" />
      )}

      <button type="button" className="try-hit" aria-label="Play or pause" onClick={toggle} />

      {active && waiting && !paused ? (
        <div className="try-spinner"><i /></div>
      ) : null}

      {active && paused && !waiting ? (
        <div className="try-glyph"><PlayIcon /></div>
      ) : null}

      <div
        ref={barRef}
        className="try-scrubwrap"
        onPointerDown={(event) => {
          event.stopPropagation();
          event.currentTarget.setPointerCapture(event.pointerId);
          draggingRef.current = true;
          seekFrom(event.clientX);
        }}
        onPointerMove={(event) => {
          if (draggingRef.current) seekFrom(event.clientX);
        }}
        onPointerUp={() => { draggingRef.current = false; }}
        onPointerCancel={() => { draggingRef.current = false; }}
      >
        <div className="try-scrub">
          <i style={{ width: `${ratio * 100}%` }} />
          <b style={{ left: `${ratio * 100}%` }} />
        </div>
      </div>
    </div>
  );
}

/* ── One question page ────────────────────────────────────────────────────── */

function QuestionPage({ question, selected, onAnswer, onNext }) {
  const answered = selected !== undefined && selected !== null;
  const correct = answered && selected === question.correctIndex;
  const isTrueFalse = question.type === "trueFalse";
  const isNumber = question.type === "number";

  const parts = String(question.prompt).split("___");

  return (
    <div className="try-card">
      <h2 className="try-prompt">
        {parts.map((part, i) => (
          <Fragment key={i}>
            {part}
            {i < parts.length - 1 ? <span className="try-blank">______</span> : null}
          </Fragment>
        ))}
      </h2>

      <div className={`try-options ${isTrueFalse ? "tf" : ""}`}>
        {question.options.map((option, i) => {
          const right = answered && i === question.correctIndex;
          const wrong = answered && i === selected && !right;
          return (
            <button
              key={i}
              type="button"
              className={`try-option ${isNumber ? "numeric" : ""} ${right ? "correct" : ""} ${wrong ? "wrong" : ""}`}
              onClick={() => (answered ? onNext() : onAnswer(i))}
            >
              <span>{option}</span>
              {right ? <CheckIcon /> : null}
              {wrong ? <CrossIcon /> : null}
            </button>
          );
        })}
      </div>

      {answered ? (
        <>
          <div className={`try-verdict ${correct ? "ok" : "no"}`}>
            <h3>
              {correct ? <CheckIcon /> : <CrossIcon />}
              {correct ? "CORRECT" : "NOT QUITE"}
            </h3>
            {question.explanation ? <p>{question.explanation}</p> : null}
          </div>
          <div className="try-spacer" />
          <button type="button" className="try-next" onClick={onNext}>
            Next <ChevronDown />
          </button>
        </>
      ) : (
        <>
          <div className="try-spacer" />
          <TopicLabel
            subject={question.subject}
            topic={question.category || question.topicName}
          />
        </>
      )}
    </div>
  );
}

/* ── One flashcard page ───────────────────────────────────────────────────────
   Two states, one gesture. Tap turns the card over; tap again moves on, which
   is also what a swipe does — so the card never needs a button of its own. */

function FlashcardPage({ card, revealed, onReveal, onNext }) {
  return (
    <div
      className="try-card"
      onClick={() => (revealed ? onNext() : onReveal())}
      role="presentation"
    >
      <h2 className="try-front">{card.front}</h2>

      {revealed ? (
        <>
          <div className="try-answer">
            <h3>ANSWER</h3>
            <p>{card.back}</p>
            {card.detail ? (
              <>
                <hr />
                <div className="detail">{card.detail}</div>
              </>
            ) : null}
          </div>

          <div className="try-spacer" />

          <button
            type="button"
            className="try-next"
            onClick={(event) => { event.stopPropagation(); onNext(); }}
          >
            Next <ChevronDown />
          </button>
        </>
      ) : (
        <>
          <div className="try-spacer" />
          <div className="try-reveal"><TapIcon /> Tap to reveal</div>
          <div className="try-spacer" />
          <TopicLabel
            subject={card.subject}
            topic={card.category || card.topicName}
          />
        </>
      )}
    </div>
  );
}

/* ── The page ─────────────────────────────────────────────────────────────── */

export default function TryMedocx() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [mode, setMode] = useState("videos");
  const [index, setIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const [answers, setAnswers] = useState({});
  const [reveals, setReveals] = useState({});

  const scrollerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetch(FEED_URL, { cache: "no-cache" })
      .then((response) => {
        if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
        return response.json();
      })
      .then((json) => { if (alive) setData(json); })
      .catch((error) => { if (alive) setLoadError(error.message || String(error)); });
    return () => { alive = false; };
  }, []);

  const items = useMemo(() => {
    if (!data) return [];
    const list = data[MODES.find((m) => m.id === mode).key];
    return Array.isArray(list) ? list : [];
  }, [data, mode]);

  // A new mode is a new feed: back to the top, exactly as picking a mode from
  // the app's tab menu reshuffles and lands you on page 0.
  useEffect(() => {
    setIndex(0);
    const root = scrollerRef.current;
    if (root) root.scrollTo({ top: 0, behavior: "auto" });
  }, [mode]);

  // Which page is in view. Queried from the DOM rather than kept in a ref array
  // so a shorter list can't leave stale nodes under observation.
  useEffect(() => {
    const root = scrollerRef.current;
    if (!root) return;
    const pages = Array.from(root.querySelectorAll(".try-page"));
    if (!pages.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.6) return;
          const next = Number(entry.target.getAttribute("data-index"));
          if (!Number.isNaN(next)) setIndex(next);
        });
      },
      { root, threshold: [0.6] }
    );

    pages.forEach((page) => observer.observe(page));
    return () => observer.disconnect();
  }, [items, mode]);

  const goTo = useCallback((target) => {
    const root = scrollerRef.current;
    if (!root) return;
    const page = root.querySelector(`.try-page[data-index="${target}"]`);
    if (page) root.scrollTo({ top: page.offsetTop, behavior: "smooth" });
  }, []);

  const next = useCallback(() => {
    setIndex((current) => {
      goTo(Math.min(current + 1, items.length - 1));
      return current;
    });
  }, [goTo, items.length]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "ArrowDown" || event.key === "PageDown") {
        event.preventDefault();
        goTo(Math.min(index + 1, items.length - 1));
      } else if (event.key === "ArrowUp" || event.key === "PageUp") {
        event.preventDefault();
        goTo(Math.max(index - 1, 0));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goTo, index, items.length]);

  const onVideos = mode === "videos";
  const percent = items.length ? ((index + 1) / items.length) * 100 : 0;
  const [emptyTitle, emptyDetail] = EMPTY_COPY[mode];

  let body;
  if (loadError) {
    body = (
      <div className={`try-note ${onVideos ? "dark" : ""}`}>
        <h2>Feed not loaded</h2>
        <p>
          Couldn&apos;t read <code>{FEED_URL}</code> ({loadError}). Run
          {" "}tools/make_try_feed.py and put the file it writes in the site&apos;s
          {" "}public folder as try-feed.json.
        </p>
      </div>
    );
  } else if (!data) {
    body = (
      <div className={`try-note ${onVideos ? "dark" : ""}`}>
        <div className="try-loading"><i /></div>
      </div>
    );
  } else if (!items.length) {
    body = (
      <div className={`try-note ${onVideos ? "dark" : ""}`}>
        <h2>{emptyTitle}</h2>
        <p>{emptyDetail}</p>
      </div>
    );
  } else {
    body = (
      <div className="try-scroller" ref={scrollerRef}>
        {items.map((item, i) => (
          <section className="try-page" data-index={i} key={item.id}>
            {mode === "videos" ? (
              <VideoPage
                video={item}
                active={i === index}
                near={Math.abs(i - index) <= 1}
                muted={muted}
                onEnded={() => goTo(Math.min(i + 1, items.length - 1))}
              />
            ) : null}

            {mode === "questions" ? (
              <QuestionPage
                question={item}
                selected={answers[item.id]}
                onAnswer={(choice) =>
                  setAnswers((current) =>
                    current[item.id] !== undefined ? current : { ...current, [item.id]: choice })
                }
                onNext={next}
              />
            ) : null}

            {mode === "flashcards" ? (
              <FlashcardPage
                card={item}
                revealed={Boolean(reveals[item.id])}
                onReveal={() =>
                  setReveals((current) => ({ ...current, [item.id]: true }))
                }
                onNext={next}
              />
            ) : null}
          </section>
        ))}
      </div>
    );
  }

  return (
    <main className="try">
      <style>{CSS}</style>

      <div className={`try-stage ${onVideos ? "" : "paper"}`}>
        <div className={`try-bar ${onVideos ? "" : "light"}`}>
          <div className="try-tabs">
            {MODES.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`try-tab ${item.id === mode ? "active" : ""}`}
                onClick={() => setMode(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="try-meta">
            <span className="try-meta-name">{data ? data.category : "Loading…"}</span>
            <span>{items.length ? `${Math.min(index + 1, items.length)} / ${items.length}` : "—"}</span>
          </div>

          <div className="try-track"><i style={{ width: `${percent}%` }} /></div>
        </div>

        {body}

        {onVideos && muted && items.length > 0 ? (
          <button type="button" className="try-soundhint" onClick={() => setMuted(false)}>
            <SpeakerOff /> Tap for sound
          </button>
        ) : null}

        {onVideos && items.length > 0 ? (
          <button
            type="button"
            className="try-sound"
            aria-label={muted ? "Turn sound on" : "Turn sound off"}
            onClick={() => setMuted((current) => !current)}
          >
            {muted ? <SpeakerOff /> : <SpeakerOn />}
          </button>
        ) : null}

        {items.length > 1 && index === 0 ? (
          <div className={`try-swipe ${onVideos ? "video" : "light"}`}>
            <ChevronDown /> Swipe
          </div>
        ) : null}
      </div>
    </main>
  );
}

/* ── Styles ───────────────────────────────────────────────────────────────────
   Local to this page and stated in full: nothing here inherits a colour, for
   the same reason the app's card views state theirs. The palette is the app's,
   converted straight across:
     paper #F1F4F8 · ink #0F0F0F · topic blue #1667C8 · navy #00274F
     correct #1D9E75 · wrong #A32D2D · accent #46B4EB
   ───────────────────────────────────────────────────────────────────────── */

const CSS = `
.try {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: calc(var(--nav-h) + 14px) 16px 20px;
  background: #e9edf2;
}

/* On a wide screen the feed keeps a phone's proportions — it IS a phone app,
   and a full-width vertical feed reads as a broken layout rather than a demo. */
.try-stage {
  position: relative;
  flex: 1;
  min-height: 0;
  width: min(420px, 100%);
  background: #000;
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0,0,0,.20), 0 0 0 1px rgba(0,0,0,.07);
}
.try-stage.paper { background: #F1F4F8; }

.try-scroller {
  position: relative;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-snap-type: y mandatory;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.try-scroller::-webkit-scrollbar { display: none; }

.try-page {
  position: relative;
  height: 100%;
  min-height: 100%;
  scroll-snap-align: start;
  scroll-snap-stop: always;
}

/* ── The floating bar ──────────────────────────────────────────────────── */
.try-bar {
  position: absolute;
  top: 0; left: 0; right: 0;
  z-index: 6;
  padding: 12px 12px 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
  pointer-events: none;
}
.try-bar > * { pointer-events: auto; }

.try-tabs {
  align-self: flex-start;
  display: flex;
  gap: 3px;
  padding: 4px;
  max-width: 100%;
  border-radius: 999px;
  background: rgba(18,18,18,.44);
  -webkit-backdrop-filter: blur(12px);
  backdrop-filter: blur(12px);
  overflow-x: auto;
  scrollbar-width: none;
}
.try-tabs::-webkit-scrollbar { display: none; }
.try-tab {
  border: 0;
  cursor: pointer;
  white-space: nowrap;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: -.01em;
  padding: 8px 13px;
  border-radius: 999px;
  background: transparent;
  color: rgba(255,255,255,.74);
  transition: background .16s ease, color .16s ease;
}
.try-tab.active { background: #fff; color: #0f0f0f; }
.try-bar.light .try-tabs { background: rgba(0,0,0,.055); }
.try-bar.light .try-tab { color: rgba(0,0,0,.55); }
.try-bar.light .try-tab.active { background: #fff; color: #0f0f0f; box-shadow: 0 1px 3px rgba(0,0,0,.10); }

.try-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  color: rgba(255,255,255,.9);
  text-shadow: 0 1px 3px rgba(0,0,0,.5);
}
.try-meta-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.try-meta span:last-child { flex: none; opacity: .8; font-variant-numeric: tabular-nums; }
.try-bar.light .try-meta { color: rgba(0,0,0,.55); text-shadow: none; }

.try-track { height: 3px; border-radius: 999px; background: rgba(255,255,255,.28); overflow: hidden; }
.try-bar.light .try-track { background: rgba(0,0,0,.10); }
.try-track i { display: block; height: 100%; border-radius: 999px; background: #46B4EB; transition: width .25s ease; }

/* ── Video page ────────────────────────────────────────────────────────── */
.try-video { position: absolute; inset: 0; background: #000; }
.try-video video,
.try-blank-frame {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  display: block;
  object-fit: contain;
  background: #000;
}
.try-hit { position: absolute; inset: 0; margin: 0; padding: 0; border: 0; background: transparent; cursor: pointer; }

.try-glyph { position: absolute; inset: 0; display: grid; place-items: center; pointer-events: none; }
.try-glyph svg { width: 62px; height: 62px; color: rgba(255,255,255,.88); filter: drop-shadow(0 3px 10px rgba(0,0,0,.45)); }

/* Delayed on purpose: anything that resolves inside half a second is a
   transition, not a wait, and a spinner that blinks on and off reads as jank. */
.try-spinner { position: absolute; inset: 0; display: grid; place-items: center; pointer-events: none; animation: tryFade .25s ease .55s both; }
.try-spinner i,
.try-loading i {
  width: 42px; height: 42px;
  border-radius: 50%;
  border: 3px solid rgba(255,255,255,.26);
  border-top-color: #fff;
  animation: trySpin .8s linear infinite;
  display: block;
}
.try-note:not(.dark) .try-loading i { border-color: rgba(0,0,0,.12); border-top-color: #1667C8; }
@keyframes trySpin { to { transform: rotate(360deg); } }
@keyframes tryFade { from { opacity: 0 } to { opacity: 1 } }

.try-sound {
  position: absolute; right: 14px; bottom: 70px; z-index: 4;
  width: 44px; height: 44px; border: 0; border-radius: 50%;
  display: grid; place-items: center; cursor: pointer;
  color: #fff; background: rgba(0,0,0,.36);
  -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px);
}
.try-sound svg { width: 20px; height: 20px; }

.try-soundhint {
  position: absolute; top: 96px; left: 50%; transform: translateX(-50%); z-index: 6;
  display: inline-flex; align-items: center; gap: 7px;
  border: 0; cursor: pointer; border-radius: 999px;
  padding: 8px 14px;
  font-family: 'Inter', sans-serif; font-size: 12.5px; font-weight: 600;
  color: #fff; background: rgba(0,0,0,.46);
  -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px);
}
.try-soundhint svg { width: 15px; height: 15px; }

.try-scrubwrap {
  position: absolute; left: 12px; right: 12px; bottom: 20px;
  height: 26px; display: flex; align-items: center;
  cursor: pointer; touch-action: none; z-index: 4;
}
.try-scrub { position: relative; width: 100%; height: 3px; border-radius: 999px; background: rgba(255,255,255,.30); box-shadow: 0 1px 2px rgba(0,0,0,.4); }
.try-scrub i { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 999px; background: #fff; }
.try-scrub b { position: absolute; top: 50%; width: 11px; height: 11px; margin: -5.5px 0 0 -5.5px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.45); }

/* ── Card pages ────────────────────────────────────────────────────────── */
.try-card {
  position: absolute; inset: 0;
  background: #F1F4F8;
  padding: 100px 20px 26px;
  display: flex; flex-direction: column;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: none;
}
.try-card::-webkit-scrollbar { display: none; }

.try-prompt,
.try-front {
  margin: 0;
  font-family: 'Fraunces', Georgia, serif;
  font-weight: 600;
  letter-spacing: -.02em;
  color: #0F0F0F;
}
.try-prompt { font-size: clamp(19px, 5.4vw, 24px); line-height: 1.26; margin-bottom: 22px; }
.try-front { font-size: clamp(21px, 5.8vw, 27px); line-height: 1.24; }
.try-blank { color: #1667C8; }

/* The card caption. Cards only — a video page carries none. */
.try-cap { margin-top: 4px; }
.try-cap-sub { font-family: 'Fraunces', Georgia, serif; font-style: italic; font-size: 15px; color: #00274F; }
.try-cap-top { font-family: 'Fraunces', Georgia, serif; font-weight: 600; font-size: clamp(19px, 5vw, 24px); line-height: 1.1; letter-spacing: -.025em; color: #1667C8; margin-top: 2px; }

.try-options { display: flex; flex-direction: column; gap: 10px; }
.try-options.tf { flex-direction: row; }
.try-option {
  flex: 1;
  display: flex; align-items: center; gap: 10px;
  text-align: left; cursor: pointer;
  font-family: 'Inter', sans-serif; font-size: 16px; font-weight: 600;
  color: #0a0a0a; background: #fff;
  border: 1.5px solid rgba(0,0,0,.15); border-radius: 14px;
  padding: 13px 16px;
  transition: border-color .15s ease, background .15s ease;
}
.try-option:hover { border-color: rgba(0,0,0,.34); }
.try-option span { flex: 1; }
.try-option svg { width: 17px; height: 17px; flex: none; }
.try-option.correct { border-width: 2px; border-color: #1D9E75; background: rgba(29,158,117,.12); color: #17805F; }
.try-option.wrong { border-width: 2px; border-color: #A32D2D; background: rgba(163,45,45,.10); color: #A32D2D; }
.try-options.tf .try-option,
.try-option.numeric {
  justify-content: center; text-align: center;
  font-family: 'Fraunces', Georgia, serif; font-size: 22px;
  padding: 22px 12px;
}

.try-verdict { margin-top: 16px; border-radius: 14px; border: 1px solid; padding: 14px; }
.try-verdict.ok { background: rgba(29,158,117,.07); border-color: rgba(29,158,117,.28); }
.try-verdict.no { background: rgba(163,45,45,.07); border-color: rgba(163,45,45,.28); }
.try-verdict h3 { display: flex; align-items: center; gap: 6px; margin: 0 0 7px; font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 800; letter-spacing: .13em; }
.try-verdict h3 svg { width: 15px; height: 15px; }
.try-verdict.ok h3 { color: #1D9E75; }
.try-verdict.no h3 { color: #A32D2D; }
.try-verdict p { margin: 0; font-family: 'Inter', sans-serif; font-size: 14.5px; line-height: 1.55; color: rgba(0,0,0,.78); }

.try-spacer { flex: 1; min-height: 12px; }

.try-next {
  align-self: flex-end; margin-top: 16px;
  display: inline-flex; align-items: center; gap: 7px;
  border: 0; cursor: pointer; border-radius: 999px;
  background: #0F0F0F; color: #fff;
  font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 700;
  padding: 11px 20px;
}
.try-next svg { width: 14px; height: 14px; }

.try-answer { margin-top: 20px; background: #fff; border: 1px solid rgba(22,103,200,.30); border-radius: 14px; padding: 16px; }
.try-answer h3 { margin: 0 0 8px; font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 800; letter-spacing: .14em; color: rgba(22,103,200,.85); }
.try-answer p { margin: 0; font-family: 'Fraunces', Georgia, serif; font-weight: 600; font-size: clamp(19px, 5vw, 24px); line-height: 1.3; color: #00274F; }
.try-answer hr { border: 0; height: 1px; margin: 12px 0; background: rgba(22,103,200,.18); }
.try-answer .detail { font-family: 'Inter', sans-serif; font-size: 14px; line-height: 1.55; color: rgba(0,0,0,.72); }

.try-reveal {
  align-self: center;
  display: inline-flex; align-items: center; gap: 7px;
  background: #fff; border: 1px solid rgba(0,0,0,.12); border-radius: 999px;
  padding: 9px 15px;
  font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600;
  color: rgba(0,0,0,.48);
}
.try-reveal svg { width: 15px; height: 15px; }

/* ── Notes, hints ──────────────────────────────────────────────────────── */
.try-note {
  position: absolute; inset: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 12px; padding: 90px 30px 40px; text-align: center;
  color: #0F0F0F;
}
.try-note.dark { color: #fff; }
.try-note h2 { margin: 0; font-family: 'Fraunces', Georgia, serif; font-weight: 600; font-size: 26px; letter-spacing: -.03em; }
.try-note p { margin: 0; font-family: 'Inter', sans-serif; font-size: 14.5px; line-height: 1.6; color: rgba(0,0,0,.5); max-width: 30ch; }
.try-note.dark p { color: rgba(255,255,255,.66); }
.try-note code { font-size: 12.5px; word-break: break-all; }

.try-swipe {
  position: absolute; left: 50%; bottom: 10px; z-index: 4;
  transform: translateX(-50%);
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px; border-radius: 999px;
  font-family: 'Inter', sans-serif; font-size: 11.5px; font-weight: 600;
  background: rgba(0,0,0,.36); color: #fff;
  pointer-events: none;
  animation: tryBob 1.9s ease-in-out infinite;
}
.try-swipe.video { bottom: 46px; }
.try-swipe.light { background: rgba(0,0,0,.06); color: rgba(0,0,0,.5); }
.try-swipe svg { width: 13px; height: 13px; }
@keyframes tryBob {
  0%, 100% { transform: translate(-50%, 0); }
  50%      { transform: translate(-50%, 4px); }
}

@media (max-width: 720px) {
  .try { padding: var(--nav-h) 0 0; }
  .try-stage { width: 100%; border-radius: 0; box-shadow: none; }
  .try-card { padding: 96px 18px 24px; }
}

@media (prefers-reduced-motion: reduce) {
  .try-swipe, .try-spinner, .try-spinner i, .try-loading i { animation: none; }
  .try-scroller { scroll-behavior: auto; }
}
`;
